import { Button } from "@/components/ui/button";
import { CalendarClock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface DailyLessonLockScreenProps {
  completedLessonName?: string | null;
  nextAvailableAt?: string | null;
}

/**
 * Shown INSTEAD of lesson content when today's lesson allowance is already
 * used. The learner sees this before any video, reading or quiz work.
 */
export function DailyLessonLockScreen({
  completedLessonName,
  nextAvailableAt,
}: DailyLessonLockScreenProps) {
  const navigate = useNavigate();
  const when = nextAvailableAt
    ? new Date(nextAvailableAt).toLocaleString(undefined, {
        weekday: "long",
        hour: "numeric",
        minute: "2-digit",
      })
    : "tomorrow morning";

  const alternatives = [
    { label: "Practice in a module", route: "/budget-builder" },
    { label: "Review your vocabulary", route: "/financial-vocabulary" },
    { label: "Open the Finance Center", route: "/finances" },
    { label: "Back to dashboard", route: "/dashboard" },
  ];

  return (
    <div className="min-h-screen bg-foreground flex items-center justify-center px-4 py-10">
      <div className="glass-card glow-border w-full max-w-lg space-y-6 p-6 text-center sm:p-8">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent/20">
          <CalendarClock className="h-8 w-8 text-accent" />
        </div>
        <div className="space-y-2">
          <h2 className="holo-text font-heading text-2xl font-bold text-background">
            That's today's lesson
          </h2>
          <p className="text-sm text-background/70">
            {completedLessonName
              ? `You already finished "${completedLessonName}" today.`
              : "You already finished a lesson today."}{" "}
            Your next lesson unlocks {when}.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {alternatives.map((a, i) => (
            <Button
              key={a.route}
              variant={i === 0 ? "default" : "outline"}
              onClick={() => navigate(a.route)}
              className="w-full justify-between font-heading"
            >
              {a.label}
              <ArrowRight className="h-4 w-4" />
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DailyLessonLockScreen;
